import {
  useCallback,
  useMemo,
  type Dispatch,
  type SetStateAction,
} from "react";
import { objectId, type CompanyLocation } from "@mysnowscape/snow-lib";
import { useQueryClient } from "@tanstack/react-query";

import {
  RouteBuilderDraft,
  RouteBuilderSession,
  RouteGenerationInputs,
} from "../../../../../types/routeBuilder";
import routeBuilder from "../../../../../services/axios/routeBuilder/index";
import flash from "../../../../../services/flash";
import { flashRouteBuilderError } from "../lib/flashRouteBuilderError";
import { resolveGenerationInputs } from "../lib/defaultInputs";

function newDraftId() {
  return objectId.newObjectId();
}

export function draftsQueryKey(companyId: string) {
  return ["route-builder", "drafts", companyId] as const;
}

type Args = {
  companyId: string;
  locations: CompanyLocation[];
  session: RouteBuilderSession | null;
  setSession: Dispatch<SetStateAction<RouteBuilderSession | null>>;
  setInputs: Dispatch<SetStateAction<RouteGenerationInputs>>;
  setSelectedRouteId: (id: string | null) => void;
  setSelectedPropertyId: (id: string | null) => void;
  setDirtyRouteIds: Dispatch<SetStateAction<Set<string>>>;
  setHasUnsavedSessionChanges: Dispatch<SetStateAction<boolean>>;
  setFocusedRouteIds: Dispatch<SetStateAction<string[] | null>>;
  setIsBusy: Dispatch<SetStateAction<boolean>>;
  activeDraftId: string | null;
  setActiveDraftId: Dispatch<SetStateAction<string | null>>;
};

export function useRouteBuilderDrafts({
  companyId,
  locations,
  session,
  setSession,
  setInputs,
  setSelectedRouteId,
  setSelectedPropertyId,
  setDirtyRouteIds,
  setHasUnsavedSessionChanges,
  setFocusedRouteIds,
  setIsBusy,
  activeDraftId,
  setActiveDraftId,
}: Args) {
  const queryClient = useQueryClient();
  const draftsKey = useMemo(() => draftsQueryKey(companyId), [companyId]);

  const draftName = useCallback(
    (draftId: string) =>
      queryClient
        .getQueryData<RouteBuilderDraft[]>(draftsKey)
        ?.find((draft) => draft.id === draftId)?.name,
    [draftsKey, queryClient],
  );

  const refreshDrafts = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: draftsKey });
  }, [draftsKey, queryClient]);

  const loadDraft = useCallback(
    async (draftId: string) => {
      setIsBusy(true);
      try {
        const next = await queryClient.fetchQuery({
          queryKey: [...draftsKey, draftId],
          queryFn: () => routeBuilder.getDraft(draftId),
        });
        if (!next) return false;
        // Set draft id before session so a CT remount mid-load still restores
        // Save changes (activeDraftId) instead of Save as draft.
        setActiveDraftId(draftId);
        const resolvedInputs = resolveGenerationInputs(next.inputs, locations);
        setSession({
          ...next,
          inputs: resolvedInputs,
        });
        setInputs(resolvedInputs);
        setSelectedRouteId(next.routes[0]?.id ?? null);
        setSelectedPropertyId(null);
        setDirtyRouteIds(new Set());
        setHasUnsavedSessionChanges(false);
        setFocusedRouteIds(null);
        return true;
      } catch (err) {
        console.error(err);
        flashRouteBuilderError(err, "Failed to open draft");
        return false;
      } finally {
        setIsBusy(false);
      }
    },
    [
      draftsKey,
      locations,
      queryClient,
      setSession,
      setInputs,
      setSelectedRouteId,
      setSelectedPropertyId,
      setDirtyRouteIds,
      setHasUnsavedSessionChanges,
      setFocusedRouteIds,
      setActiveDraftId,
      setIsBusy,
    ],
  );

  const saveSessionAsDraft = useCallback(
    async (name: string) => {
      if (!session) return false;
      setIsBusy(true);
      try {
        const draft = await routeBuilder.putDraft({
          draftId: newDraftId(),
          companyId,
          name,
          session,
        });
        setActiveDraftId(draft.id);
        setHasUnsavedSessionChanges(false);
        refreshDrafts();
        void flash.success(`Saved draft “${draft.name}”.`);
        return true;
      } catch (err) {
        console.error(err);
        flashRouteBuilderError(err, "Failed to save draft");
        return false;
      } finally {
        setIsBusy(false);
      }
    },
    [
      session,
      companyId,
      refreshDrafts,
      setIsBusy,
      setActiveDraftId,
      setHasUnsavedSessionChanges,
    ],
  );

  const saveActiveDraftChanges = useCallback(async () => {
    if (!session || !activeDraftId) return false;
    setIsBusy(true);
    try {
      const draft = await routeBuilder.putDraft({
        draftId: activeDraftId,
        companyId,
        name: draftName(activeDraftId) ?? "Untitled draft",
        session,
      });
      setHasUnsavedSessionChanges(false);
      refreshDrafts();
      void flash.success(`Saved changes to “${draft.name}”.`);
      return true;
    } catch (err) {
      console.error(err);
      flashRouteBuilderError(err, "Failed to save draft changes");
      return false;
    } finally {
      setIsBusy(false);
    }
  }, [
    session,
    activeDraftId,
    draftName,
    companyId,
    refreshDrafts,
    setIsBusy,
    setHasUnsavedSessionChanges,
  ]);

  return {
    refreshDrafts,
    loadDraft,
    saveSessionAsDraft,
    saveActiveDraftChanges,
  };
}
